/**
 * The whole router.
 *
 * This host is TRANSACTIONAL: it answers the links the Tadbeer mobile app and
 * the operator's SMS point at, and nothing else. Anything unrecognised lands on
 * the brand gate, which tells the customer to open the app — **not a 404**,
 * because every visitor here arrived from a link WE sent them, and telling them
 * "page not found" would be blaming them for our own stale link.
 *
 * ── Why this is a separate deployable ──────────────────────────────────────
 * The console is the operator's tool and sits behind a login. This host is
 * opened by CUSTOMERS with no account, from an SMS or a payment WebView. One
 * deployment, one domain, one CSP — and a payment page that cannot inherit an
 * operator's session.
 *
 * ── What was removed on the way in ─────────────────────────────────────────
 * The source (`wt-taheel-landing`) was a full customer web app: login,
 * browsing, a booking wizard, a wallet, an account — sixteen feature areas, of
 * which four are kept. The same trim was made for Watan Care first and this
 * follows it; the reasoning is in `routes/routeNames.ts`.
 */
import { Suspense, lazy } from 'react';
import { Routes as ReactRoutes, Route } from 'react-router-dom';

import { Routes } from './routes/routeNames';
import { LoadingScreen } from './components/shared/LoadingScreen';

// Lazy on purpose: a customer opening a payment link should not download the
// Google Maps bundle, and a customer opening a confirm-location link should not
// download the HyperPay widget page.
const PayCheckoutPage = lazy(() => import('./features/payment/PayCheckoutPage'));
const PayReturnPage = lazy(() => import('./features/payment/PayReturnPage'));
const LocationConfirmPage = lazy(
  () => import('./features/location-confirm/LocationConfirmPage'),
);
const TermsPage = lazy(() => import('./features/legal/TermsPage'));
const PrivacyPage = lazy(() => import('./features/legal/PrivacyPage'));
const BrandGatePage = lazy(() => import('./features/system/BrandGatePage'));

export default function App() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <ReactRoutes>
        {/* Payment — the reason the app points a WebView at this host. */}
        <Route path={Routes.payCheckout.path} element={<PayCheckoutPage />} />
        <Route path={Routes.payReturn.path} element={<PayReturnPage />} />

        {/* Location-confirm — the reason this host is SMSed to customers. */}
        <Route
          path={Routes.confirmLocation.path}
          element={<LocationConfirmPage />}
        />

        {/* Terms — BACKEND-FED, so it is Tadbeer's own document by
            construction rather than by find-and-replace.

            ⚠️ `/legal/terms` and `/legal/privacy` are deliberately ABSENT.
            The source hardcoded full Terms and a Privacy Policy naming
            **Watan First Human Resources** as the operating party, with its
            commercial registration (1010589187) and VAT number — measured,
            not assumed. Renaming a company inside a legal document and
            serving it from tadbeerco.com would be publishing a false legal
            claim, so both pages were deleted rather than rebranded.

            The privacy policy came back on 2026-10-31 the same way `/terms`
            did: `/privacy` reads the `privacy_policy` terms document the
            console edits, and shows a fallback until one is published. */}
        <Route path={Routes.terms.path} element={<TermsPage />} />
        <Route path={Routes.privacy.path} element={<PrivacyPage />} />

        {/* Root and everything unrecognised. */}
        <Route path="*" element={<BrandGatePage />} />
      </ReactRoutes>
    </Suspense>
  );
}
